<script lang="ts">
  import { cn } from '$lib/utils';
  import { StrapiParse, type BlocksDynamicZone } from 'query-strapi-v4';
  import { onMount } from 'svelte';
  import { afterNavigate } from '$app/navigation';
  import { page } from '$app/stores';
  /** @type {string} */
  let hash: string = '';
  /** @type {number} */
  let height: number = 0;
  /** @type {HTMLElement} */
  let content: HTMLElement;
  /** @type {NodeListOf<HTMLElement>} */
  let headings: NodeListOf<HTMLElement>;
  /** @type {number[]} */
  let positions: number[] = [];

  onMount(async () => {
    update();
    highlight();
  });

  afterNavigate(() => {
    update();
    highlight();
  });

  const linkClassName =
    'block px-1 py-0 text-zinc-900 dark:text-zinc-300 dark:hover:text-orange-600 hover:text-orange-600';
  const activeLinkClassName =
    'text-orange-600 dark:bg-gray-1000 bg-zinc-200/50 border-l border-orange-600';

  function update() {
    const article = document.querySelector<HTMLElement>('article');
    if (article) {
      content = article;
    }
    const { top } = content.getBoundingClientRect();
    headings = content.querySelectorAll('h2[id], h3[id], h4[id]');
    positions = Array.from(headings).map((heading) => {
      const style = getComputedStyle(heading);
      return heading.getBoundingClientRect().top - parseFloat(style.scrollMarginTop) - top;
    });
    height = window.innerHeight;
  }
  function highlight() {
    const { top, bottom } = content.getBoundingClientRect();
    let i = headings.length;
    while (i--) {
      if (bottom - height < 50 || positions[i] + top < 100) {
        const heading = headings[i];
        hash = `#${heading.id}`;
        return;
      }
    }
    hash = '';
  }
  /** @param {URL} url */
  function select(url: URL) {
    // belt...
    setTimeout(() => {
      hash = url.hash;
    });
    // ...and braces
    window.addEventListener(
      'scroll',
      () => {
        hash = url.hash;
      },
      { once: true }
    );
  }
  type AnchorNavigationOptions = {
    collapseChildren?: boolean;
    includeHeadings: Array<number>;
  };

  export let blocks: BlocksDynamicZone[];

  export let options: AnchorNavigationOptions = {
    collapseChildren: false,
    includeHeadings: [2, 3]
  };

  $: toc = StrapiParse.BlockParser.getTableOfContents(blocks);

  //TODO: only show section when parent is navigated to
  const { collapseChildren } = options;

  // Reduce the results to just the blocks that can have headings
  const anchor = (path: string) => {
    return `#${path}`;
  };
</script>

<svelte:window on:scroll={highlight} on:resize={update} on:hashchange={() => select($page.url)} />

<nav class="w-56 pb-48">
  <p class="font-bold dark:text-zinc-300">On this page</p>
  {#each toc as heading (heading.id)}
    <ol class="mt-4 space-y-3 text-sm">
      <li
        class={cn({
          'pl-4': heading.data.level === 3
        })}
      >
        <a
          href={anchor(heading.anchorId)}
          class={cn(linkClassName, anchor(heading.anchorId) === hash ? activeLinkClassName : '')}
        >
          {@html heading.data.text}
        </a>
        <ol class={cn('mt-2 space-y-3 pl-5 text-zinc-500 dark:text-zinc-400')}>
          {#each heading.children as child (child.id)}
            <li
              class={cn({
                'pl-6': child.data.level === 4
              })}
            >
              <a
                href={anchor(child.anchorId)}
                class={cn(
                  linkClassName,
                  anchor(child.anchorId) === hash ? activeLinkClassName : ''
                )}
              >
                {@html child.data.text}
              </a>
            </li>
          {/each}
        </ol>
      </li>
    </ol>
  {/each}
</nav>
